import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoSpinner, DiscoTextButton } from "@disco-ui"
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay"
import TrainingSourceTableItem from "./TrainingSourceTableItem"
import { TrainingDataItemsPaginationQuery } from "./__generated__/TrainingDataItemsPaginationQuery.graphql"
import { TrainingDataItemsQuery } from "./__generated__/TrainingDataItemsQuery.graphql"
import { TrainingDataItems_PaginationFragment$key } from "./__generated__/TrainingDataItems_PaginationFragment.graphql"

const ITEMS_PER_LOAD = 3

interface Props {
  trainingSourceId: GlobalID
}

function TrainingDataItems({ trainingSourceId }: Props) {
  const classes = useStyles()
  const { trainingSource } = useLazyLoadQuery<TrainingDataItemsQuery>(
    graphql`
      query TrainingDataItemsQuery($id: ID!, $first: Int!, $after: String) {
        trainingSource: node(id: $id) {
          ... on TrainingSource {
            ...TrainingDataItems_PaginationFragment
              @arguments(first: $first, after: $after)
          }
        }
      }
    `,
    {
      id: trainingSourceId,
      first: ITEMS_PER_LOAD,
    },
    {
      fetchPolicy: "network-only",
    }
  )

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment<
    TrainingDataItemsPaginationQuery,
    TrainingDataItems_PaginationFragment$key
  >(
    graphql`
      fragment TrainingDataItems_PaginationFragment on TrainingSource
      @refetchable(queryName: "TrainingDataItemsPaginationQuery")
      @argumentDefinitions(first: { type: "Int!" }, after: { type: "String" }) {
        trainingData(first: $first, after: $after)
          @connection(key: "TrainingDataItems_trainingData") {
          edges {
            node {
              id
              url
              name
            }
          }
        }
      }
    `,
    trainingSource
  )

  const trainingData = Relay.connectionToArray(data?.trainingData)

  return (
    <div>
      {trainingData.map((td) => (
        <TrainingSourceTableItem key={td.id} name={td.name} url={td.url} subItem />
      ))}

      {hasNext && (
        <div className={classes.loadNextContainer}>
          {isLoadingNext ? (
            <DiscoSpinner />
          ) : (
            <DiscoTextButton
              onClick={() => loadNext(ITEMS_PER_LOAD)}
              textVariant={"body-sm-600"}
            >
              {"Load More..."}
            </DiscoTextButton>
          )}
        </div>
      )}
    </div>
  )
}

const useStyles = makeUseStyles((theme) => ({
  loadNextContainer: {
    display: "flex",
    justifyContent: "flex-start",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.groovy.neutral[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}))

export default Relay.withSkeleton({
  component: TrainingDataItems,
  skeleton: () => <DiscoSpinner />,
})
